/**
 * Landing Page component
 * The main marketing page for VarsityLink - updated with company section
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Users, 
  Calendar, 
  BookOpen, 
  ArrowRight, 
  Menu, 
  X, 
  Instagram, 
  Mail, 
  Phone, 
  HelpCircle, 
  ShoppingBag,
  Book,
  Search,
  Award,
  UserPlus,
  PenTool,
  MessageCircle,
  Clock,
  Building,
  Briefcase,
  Target,
  TrendingUp,
  Star
} from 'lucide-react';

/**
 * Landing Page Component
 * 
 * @returns {JSX.Element} Landing page component
 */
const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('students'); // 'students' or 'companies'
  const navigate = useNavigate();

  const navigateToLogin = () => navigate('/auth', { state: { showLogin: true } });
  const navigateToRegister = () => navigate('/auth', { state: { showLogin: false } });
  const navigateToCompanyLogin = () => navigate('/company/auth', { state: { showLogin: true } });
  const navigateToCompanyRegister = () => navigate('/company/auth', { state: { showLogin: false } });

  // Student features section data
  const studentFeatures = [
    {
      icon: <Users className="w-6 h-6 text-green-500" />,
      title: "Connect with Students",
      description: "Build meaningful connections with fellow students from your university and others across South Africa",
    },
    {
      icon: <BookOpen className="w-6 h-6 text-green-500" />,
      title: "Study Partners",
      description: "Find study partners who take the same courses as you to collaborate and excel together",
    },
    {
      icon: <Calendar className="w-6 h-6 text-green-500" />,
      title: "Campus Events",
      description: "Stay updated with academic and social events happening around your campus",
    },
    {
      icon: <Book className="w-6 h-6 text-green-500" />,
      title: "Assignment Hub",
      description: "Get help with challenging assignments from knowledgeable peers who understand your coursework",
    },
    {
      icon: <Search className="w-6 h-6 text-green-500" />,
      title: "Find Tutors",
      description: "Connect with qualified tutors for personalized academic support and improved learning",
    },
    {
      icon: <Award className="w-6 h-6 text-green-500" />,
      title: "Career Opportunities",
      description: "Discover graduate programs and internships tailored specifically for university students",
    }
  ];

  // Company features section data
  const companyFeatures = [
    {
      icon: <Target className="w-6 h-6 text-green-500" />,
      title: "Target Top Talent",
      description: "Access a pool of motivated university students from South Africa's leading institutions",
    },
    {
      icon: <Briefcase className="w-6 h-6 text-green-500" />,
      title: "Post Job Opportunities",
      description: "Create compelling job postings for internships, graduate programs, and entry-level positions",
    },
    {
      icon: <Users className="w-6 h-6 text-green-500" />,
      title: "Streamlined Applications",
      description: "Manage applications efficiently with our integrated candidate management system",
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-green-500" />,
      title: "Brand Visibility",
      description: "Increase your company's visibility among the next generation of skilled professionals",
    },
    {
      icon: <Award className="w-6 h-6 text-green-500" />,
      title: "Quality Candidates",
      description: "Connect with pre-screened candidates who are serious about starting their careers",
    },
    {
      icon: <Clock className="w-6 h-6 text-green-500" />,
      title: "Efficient Hiring",
      description: "Reduce time-to-hire with our streamlined recruitment tools and processes",
    }
  ];

  // Academic features section data
  const academicFeatures = [
    {
      icon: <Book className="w-10 h-10 text-green-500" />,
      title: "Assignment Hub",
      description: "Get help with challenging assignments from peers who understand your coursework, with strong policies against plagiarism to maintain academic integrity."
    },
    {
      icon: <Search className="w-10 h-10 text-green-500" />,
      title: "Find Tutors",
      description: "Easily find and connect with qualified tutors from universities across South Africa. Filter by course, university, and read reviews from other students."
    },
    {
      icon: <UserPlus className="w-10 h-10 text-green-500" />,
      title: "Study Partners",
      description: "Connect with students taking the same courses as you, form study groups, and collaborate effectively on coursework and exam preparation."
    },
    {
      icon: <Award className="w-10 h-10 text-green-500" />,
      title: "Career Opportunities",
      description: "Explore curated graduate programs and internship opportunities from companies looking to recruit top university talent."
    }
  ];

  // Social features section data
  const socialFeatures = [
    {
      icon: <MessageCircle className="w-10 h-10 text-green-500" />,
      title: "Social Feed",
      description: "Share updates, thoughts, and experiences with the student community in a safe, students-only environment."
    },
    {
      icon: <Calendar className="w-10 h-10 text-green-500" />,
      title: "Campus Events",
      description: "Discover and participate in academic and social events happening on campuses across South Africa."
    },
    {
      icon: <PenTool className="w-10 h-10 text-green-500" />,
      title: "Daily Academic Quiz",
      description: "Test your knowledge and engage with fun, educational daily quizzes across various academic disciplines."
    },
    {
      icon: <Clock className="w-10 h-10 text-green-500" />,
      title: "Balance Study & Social Life",
      description: "VarsityLink helps you maintain a healthy balance between academic excellence and meaningful social connections."
    }
  ];

  // Navigation links
  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'For Students', href: '#students' },
    { name: 'For Companies', href: '#companies' },
    { name: 'Support', href: '#support' },
  ];

  // Custom TikTok Icon Component
  const TikTokIcon = ({ className }) => (
    <svg 
      className={className} 
      viewBox="0 0 24 24" 
      fill="currentColor"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                  <span className="text-white font-bold text-xl transform -rotate-12">V</span>
                </div>
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              </div>
              <span className="text-xl font-bold text-gray-800">
                <span className="text-green-500">Varsity</span>Link
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-gray-600 hover:text-green-500 transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="https://www.instagram.com/varsitylink_za/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-green-500"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@varsitylink"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-green-500"
              >
                <TikTokIcon className="w-5 h-5" />
              </a>
              
              {/* Login Dropdown */}
              <div className="relative group">
                <button className="text-gray-600 hover:text-green-500 flex items-center space-x-1">
                  <span>Sign In</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-2">
                    <button
                      onClick={navigateToLogin}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Student Portal
                    </button>
                    <button
                      onClick={navigateToCompanyLogin}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Company Portal
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="relative group">
                <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl flex items-center space-x-1">
                  <span>Get Started</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-2">
                    <button
                      onClick={navigateToRegister}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Join as Student
                    </button>
                    <button
                      onClick={navigateToCompanyRegister}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Join as Company
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-green-500"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-2 text-gray-600 hover:text-green-500"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="https://www.instagram.com/varsitylink_za/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 py-2 text-gray-600 hover:text-green-500"
              >
                <Instagram className="w-5 h-5" />
                <span>Follow us on Instagram</span>
              </a>
              <a
                href="https://www.tiktok.com/@varsitylink"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 py-2 text-gray-600 hover:text-green-500"
              >
                <TikTokIcon className="w-5 h-5" />
                <span>Follow us on TikTok</span>
              </a>
              <div className="space-y-2 pt-4 border-t border-gray-100">
                <div className="font-medium text-gray-700">Students</div>
                <button
                  onClick={navigateToLogin}
                  className="block w-full text-left py-2 text-gray-600 hover:text-green-500"
                >
                  Student Sign In
                </button>
                <button
                  onClick={navigateToRegister}
                  className="block w-full bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl"
                >
                  Join as Student
                </button>
                
                <div className="font-medium text-gray-700 pt-2">Companies</div>
                <button
                  onClick={navigateToCompanyLogin}
                  className="block w-full text-left py-2 text-gray-600 hover:text-green-500"
                >
                  Company Sign In
                </button>
                <button
                  onClick={navigateToCompanyRegister}
                  className="block w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl"
                >
                  Join as Company
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-green-600/20 z-0" />
        <div className="relative z-10 container mx-auto px-4 pt-20 pb-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
              {/* Tab Selector */}
              <div className="flex items-center space-x-4 mb-6">
                <button
                  onClick={() => setActiveTab('students')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeTab === 'students'
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <GraduationCap className="w-4 h-4 inline mr-2" />
                  For Students
                </button>
                <button
                  onClick={() => setActiveTab('companies')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeTab === 'companies'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Building className="w-4 h-4 inline mr-2" />
                  For Companies
                </button>
              </div>

              {activeTab === 'students' ? (
                <>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                    Connect, Study, and Thrive Together
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 mb-8">
                    Join VarsityLink to connect with fellow students, find study partners, 
                    access academic resources, and discover career opportunities.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <button
                      onClick={navigateToRegister}
                      className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center space-x-2"
                    >
                      <span>Join as Student</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={navigateToLogin}
                      className="w-full sm:w-auto border border-gray-300 hover:border-green-500 hover:text-green-500 px-6 py-3 rounded-xl font-medium"
                    >
                      Student Sign In
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                    Find Top University Talent
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 mb-8">
                    Connect with motivated students from South Africa's leading universities. 
                    Post jobs, manage applications, and build your future workforce.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <button
                      onClick={navigateToCompanyRegister}
                      className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center space-x-2"
                    >
                      <span>Join as Company</span>
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={navigateToCompanyLogin}
                      className="w-full sm:w-auto border border-gray-300 hover:border-blue-500 hover:text-blue-500 px-6 py-3 rounded-xl font-medium"
                    >
                      Company Sign In
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="hidden md:block">
              <img 
                src="/images/5.png" 
                alt="VarsityLink App Preview" 
                className="rounded-2xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-300 w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div id="about" className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Why VarsityLink?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Created by students for students, VarsityLink addresses the unique needs of university life 
              by providing a safe, students-only platform for both academic excellence and social connection. 
              We also connect companies with the next generation of skilled professionals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(activeTab === 'students' ? studentFeatures : companyFeatures).map((feature, index) => (
              <div key={index} className="group bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Students Features Section */}
      <div id="students" className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm inline-flex items-center mb-4">
              <GraduationCap className="w-4 h-4 mr-2" />
              For Students
            </span>
            <h2 className="text-3xl font-bold mb-6">Boost Your Academic Journey</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              VarsityLink provides the tools and connections you need to excel in your studies, 
              access quality academic support, and prepare for your future career.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {academicFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-50 p-3 rounded-xl">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          

        </div>
      </div>

      {/* Companies Section */}
      <div id="companies" className="bg-white py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm inline-flex items-center mb-4">
              <Building className="w-4 h-4 mr-2" />
              For Companies
            </span>
            <h2 className="text-3xl font-bold mb-6">Access Top University Talent</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Connect with motivated students from South Africa's leading universities. 
              Post job opportunities, manage applications, and build relationships with future leaders.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {companyFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="bg-white p-3 rounded-xl shadow-sm">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Company CTA */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Find Your Next Hire?</h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Join leading companies who trust VarsityLink to connect them with South Africa's 
                brightest university talent. Post your first job today.
              </p>
              <button
                onClick={navigateToCompanyRegister}
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-xl font-medium shadow-lg"
              >
                Start Hiring Today
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Social Features Section */}
      <div className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm inline-flex items-center mb-4">
              <Users className="w-4 h-4 mr-2" />
              Social Connections
            </span>
            <h2 className="text-3xl font-bold mb-6">Balance Your Student Life</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We believe in the importance of social connections and a balanced university experience. 
              VarsityLink helps you connect with like-minded students and build lasting relationships.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {socialFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl hover:shadow-md transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="bg-gray-50 p-3 rounded-xl shadow-sm">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-white py-20 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">What Our Community Says</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Student Testimonial */}
            <div className="bg-gray-50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <div className="flex text-yellow-400 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"VarsityLink helped me find the perfect study group for my Computer Science courses. The academic resources are amazing, and I've made great connections!"</p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="font-semibold">Sipho M.</div>
                  <div className="text-sm text-gray-500">UCT, Computer Science</div>
                </div>
              </div>
            </div>
            
            {/* Company Testimonial */}
            <div className="bg-gray-50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <div className="flex text-yellow-400 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"We've hired 5 excellent graduates through VarsityLink this year. The quality of candidates and the streamlined process makes recruitment so much easier."</p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="font-semibold">Sarah K.</div>
                  <div className="text-sm text-gray-500">HR Manager, TechCorp SA</div>
                </div>
              </div>
            </div>
            
            {/* Tutor Testimonial */}
            <div className="bg-gray-50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <div className="flex text-yellow-400 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic">"As a tutor on VarsityLink, I've been able to connect with students who truly want to learn. The platform makes it easy to manage my schedule and connect with students."</p>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="font-semibold">Thandi N.</div>
                  <div className="text-sm text-gray-500">Wits, Engineering Tutor</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-green-500 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Transform Your Future?</h2>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of students and companies across South Africa who are already using VarsityLink
            to build connections, advance careers, and shape the future.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={navigateToRegister}
              className="bg-white text-green-500 hover:bg-gray-100 px-8 py-3 rounded-xl font-medium shadow-lg"
            >
              Join as Student
            </button>
            <button
              onClick={navigateToCompanyRegister}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-xl font-medium shadow-lg"
            >
              Join as Company
            </button>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div id="support" className="bg-white py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Need Help?</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              <div className="bg-gray-50 p-6 rounded-2xl text-center flex flex-col items-center">
                <Mail className="w-8 h-8 text-green-500 mb-4" />
                <h3 className="font-semibold mb-2">Email Support</h3>
                <p className="text-gray-500 text-sm">admin@varsitylink.co.za</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl text-center flex flex-col items-center">
                <Instagram className="w-8 h-8 text-green-500 mb-4" />
                <h3 className="font-semibold mb-2">Instagram</h3>
                <a 
                  href="https://www.instagram.com/varsitylink_za/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-600 text-sm"
                >
                  @varsitylink_za
                </a>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl text-center flex flex-col items-center">
                <TikTokIcon className="w-8 h-8 text-green-500 mb-4" />
                <h3 className="font-semibold mb-2">TikTok</h3>
                <a 
                  href="https://www.tiktok.com/@varsitylink"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-600 text-sm"
                >
                  @varsitylink
                </a>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl text-center flex flex-col items-center">
                <HelpCircle className="w-8 h-8 text-green-500 mb-4" />
                <h3 className="font-semibold mb-2">Help Center</h3>
                <p className="text-gray-600 text-sm">FAQs and Guides</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center transform rotate-12 shadow-lg">
                    <span className="text-white font-bold text-xl transform -rotate-12">V</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                </div>
                <span className="text-xl font-bold text-gray-800">
                  <span className="text-green-500">Varsity</span>Link
                </span>
              </div>
              <p className="text-gray-600 mt-4">
                The ultimate platform for university students and companies across South Africa to connect, 
                collaborate, and build the future together.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-gray-900 mb-4">For Students</h3>
              <ul className="space-y-2">
                <li><a href="#students" className="text-gray-600 hover:text-green-500">Study Partners</a></li>
                <li><a href="#students" className="text-gray-600 hover:text-green-500">Assignment Hub</a></li>
                <li><a href="#students" className="text-gray-600 hover:text-green-500">Find Tutors</a></li>
                <li><a href="#students" className="text-gray-600 hover:text-green-500">Career Opportunities</a></li>
                <li><button onClick={navigateToRegister} className="text-gray-600 hover:text-green-500">Join as Student</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-gray-900 mb-4">For Companies</h3>
              <ul className="space-y-2">
                <li><a href="#companies" className="text-gray-600 hover:text-green-500">Post Jobs</a></li>
                <li><a href="#companies" className="text-gray-600 hover:text-green-500">Find Talent</a></li>
                <li><a href="#companies" className="text-gray-600 hover:text-green-500">Manage Applications</a></li>
                <li><a href="#companies" className="text-gray-600 hover:text-green-500">University Partnerships</a></li>
                <li><button onClick={navigateToCompanyRegister} className="text-gray-600 hover:text-green-500">Join as Company</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-gray-900 mb-4">Connect</h3>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="mailto:admin@varsitylink.co.za"
                    className="flex items-center text-gray-600 hover:text-green-500"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    <span>admin@varsitylink.co.za</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.instagram.com/varsitylink_za/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-600 hover:text-green-500"
                  >
                    <Instagram className="w-5 h-5 mr-2" />
                    <span>@varsitylink_za</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.tiktok.com/@varsitylink"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-600 hover:text-green-500"
                  >
                    <TikTokIcon className="w-5 h-5 mr-2" />
                    <span>@varsitylink</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              © {new Date().getFullYear()} VarsityLink. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <a href="#" className="text-sm text-gray-600 hover:text-green-500">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-600 hover:text-green-500">Terms of Service</a>
              <a href="#" className="text-sm text-gray-600 hover:text-green-500">Company Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;